/**
 * Org Admin Tools API Service
 *
 * API calls for organization-level tool management.
 * Used by tenant_admin users to manage tool visibility for their organization.
 */

import { apiGet, apiPost, apiPatch, apiDelete } from "./api";
import type {
  TenantTool,
  TenantToolListResponse,
  TenantToolStats,
  TenantToolFilters,
  ToolApprovalUpdate,
  ToolApprovalResponse,
  TenantPrivateToolCreate,
  TenantPrivateToolUpdate,
  ApiTenantTool,
  ApiTenantToolListResponse,
  ApiTenantToolStats,
  LibraryDifficulty,
} from "@/types/adminTools";

// =============================================================================
// Response Transformers
// =============================================================================

function transformTenantTool(tool: ApiTenantTool): TenantTool {
  return {
    id: tool.id,
    title: tool.title,
    description: tool.description,
    externalUrl: tool.external_url,
    topics: tool.topics,
    difficulty: tool.difficulty as LibraryDifficulty,
    author: tool.author,
    featured: tool.featured,
    thumbnailUrl: tool.thumbnail_url,
    isPublic: tool.is_public,
    approvalStatus: tool.approval_status,
    approvedBy: tool.approved_by,
    approvedAt: tool.approved_at,
    userBookmarkCount: tool.user_bookmark_count,
    createdAt: tool.created_at,
    updatedAt: tool.updated_at,
  };
}

function transformTenantToolList(
  response: ApiTenantToolListResponse,
): TenantToolListResponse {
  return {
    tools: response.tools.map(transformTenantTool),
    total: response.total,
    page: response.page,
    pageSize: response.page_size,
    totalPages: response.total_pages,
  };
}

function transformTenantToolStats(stats: ApiTenantToolStats): TenantToolStats {
  return {
    totalTools: stats.total_tools,
    publicTools: stats.public_tools,
    privateTools: stats.private_tools,
    hiddenTools: stats.hidden_tools,
    totalBookmarks: stats.total_bookmarks,
  };
}

// =============================================================================
// API Calls
// =============================================================================

/**
 * List all tools with tenant-specific approval status
 */
export async function getOrgAdminTools(
  filters?: TenantToolFilters,
): Promise<TenantToolListResponse> {
  const params: Record<string, unknown> = {};

  if (filters?.search) params.search = filters.search;
  if (filters?.visibility) params.visibility = filters.visibility;
  if (filters?.toolType) params.tool_type = filters.toolType;
  if (filters?.page) params.page = filters.page;
  if (filters?.pageSize) params.page_size = filters.pageSize;

  const response = await apiGet<ApiTenantToolListResponse>(
    "/tenant-admin/tools",
    params,
  );
  return transformTenantToolList(response);
}

/**
 * Get tool statistics for the tenant
 */
export async function getOrgAdminToolStats(): Promise<TenantToolStats> {
  const response = await apiGet<ApiTenantToolStats>(
    "/tenant-admin/tools/stats",
  );
  return transformTenantToolStats(response);
}

/**
 * Get a specific tool with tenant-specific data
 */
export async function getOrgAdminTool(toolId: string): Promise<TenantTool> {
  const response = await apiGet<ApiTenantTool>(`/tenant-admin/tools/${toolId}`);
  return transformTenantTool(response);
}

/**
 * Update a tool's approval status (hide/show)
 */
export async function updateToolApproval(
  toolId: string,
  data: ToolApprovalUpdate,
): Promise<ToolApprovalResponse> {
  const response = await apiPatch<{
    tool_id: string;
    status: string;
    approved_by: string;
    approved_at: string;
    notes: string | null;
  }>(`/tenant-admin/tools/${toolId}/approval`, {
    status: data.status,
    notes: data.notes,
  });

  return {
    toolId: response.tool_id,
    status: response.status as ToolApprovalResponse["status"],
    approvedBy: response.approved_by,
    approvedAt: response.approved_at,
    notes: response.notes,
  };
}

/**
 * Create a private tool for the tenant
 */
export async function createPrivateTool(
  data: TenantPrivateToolCreate,
): Promise<TenantTool> {
  const response = await apiPost<ApiTenantTool>("/tenant-admin/tools", {
    title: data.title,
    description: data.description,
    external_url: data.externalUrl,
    topics: data.topics,
    difficulty: data.difficulty,
    author: data.author,
    thumbnail_url: data.thumbnailUrl,
    featured: data.featured,
  });
  return transformTenantTool(response);
}

/**
 * Update a private tool owned by the tenant
 */
export async function updatePrivateTool(
  toolId: string,
  data: TenantPrivateToolUpdate,
): Promise<TenantTool> {
  const apiData: Record<string, unknown> = {};

  if (data.title !== undefined) apiData.title = data.title;
  if (data.description !== undefined) apiData.description = data.description;
  if (data.externalUrl !== undefined) apiData.external_url = data.externalUrl;
  if (data.topics !== undefined) apiData.topics = data.topics;
  if (data.difficulty !== undefined) apiData.difficulty = data.difficulty;
  if (data.author !== undefined) apiData.author = data.author;
  if (data.thumbnailUrl !== undefined)
    apiData.thumbnail_url = data.thumbnailUrl;
  if (data.featured !== undefined) apiData.featured = data.featured;

  const response = await apiPatch<ApiTenantTool>(
    `/tenant-admin/tools/${toolId}`,
    apiData,
  );
  return transformTenantTool(response);
}

/**
 * Delete a private tool owned by the tenant
 */
export async function deletePrivateTool(toolId: string): Promise<void> {
  await apiDelete<void>(`/tenant-admin/tools/${toolId}`);
}
